import React, { useMemo } from "react";
import { useApp } from "../context/AppContext";
import { CATEGORY_COLORS } from "../data/mockData";
import {
  TrendingUp, TrendingDown, Award, AlertTriangle,
  Target, Zap, BarChart2, PieChart
} from "lucide-react";
import { RadarChart, Radar, PolarGrid, PolarAngleAxis, ResponsiveContainer, Tooltip } from "recharts";

const tooltipStyle = {
  backgroundColor: "#131520", border: "1px solid #1E2235",
  borderRadius: "12px", color: "#fff",
  fontFamily: "'DM Sans', sans-serif", fontSize: "13px",
};

export default function Insights() {
  const { state } = useApp();
  const txns = state.transactions;

  const stats = useMemo(() => {
    const income = txns.filter((t) => t.type === "income").reduce((s, t) => s + t.amount, 0);
    const expenses = txns.filter((t) => t.type === "expense").reduce((s, t) => s + t.amount, 0);

    const catMap = {};
    txns.filter((t) => t.type === "expense").forEach((t) => {
      catMap[t.category] = (catMap[t.category] || 0) + t.amount;
    });
    const categories = Object.entries(catMap)
      .map(([name, value]) => ({ name, value: Math.round(value), color: CATEGORY_COLORS[name] }))
      .sort((a, b) => b.value - a.value);

    const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun"];
    const monthly = months.map((month, idx) => {
      const monthNum = idx + 1;
      const t = txns.filter((x) => parseInt(x.date.split("-")[1]) === monthNum);
      const inc = t.filter((x) => x.type === "income").reduce((s, x) => s + x.amount, 0);
      const exp = t.filter((x) => x.type === "expense").reduce((s, x) => s + x.amount, 0);
      return { month, income: Math.round(inc), expenses: Math.round(exp) };
    }).filter((m) => m.income > 0 || m.expenses > 0);

    const latestMonth = monthly[monthly.length - 1];
    const prevMonth = monthly[monthly.length - 2];
    const expenseChange = prevMonth
      ? (((latestMonth?.expenses - prevMonth?.expenses) / prevMonth?.expenses) * 100).toFixed(1)
      : 0;

    const radarData = categories.slice(0, 6).map(({ name, value }) => ({
      subject: name.split(" ")[0], value, fullMark: categories[0]?.value || 1000,
    }));

    return { income, expenses, categories, monthly, latestMonth, expenseChange, radarData };
  }, [txns]);

  const savingsRate = stats.income > 0 ? (((stats.income - stats.expenses) / stats.income) * 100).toFixed(1) : 0;

  const insights = [
    {
      icon: Award,
      color: "gold",
      title: "Top Spending Category",
      value: stats.categories[0]?.name || "—",
      detail: stats.categories[0] ? `$${stats.categories[0].value.toLocaleString()} spent` : "No data",
      bg: "bg-accent-gold/5", border: "border-accent-gold/20", iconColor: "text-accent-gold",
    },
    {
      icon: savingsRate > 20 ? TrendingUp : TrendingDown,
      color: savingsRate > 20 ? "green" : "red",
      title: "Savings Rate",
      value: `${savingsRate}%`,
      detail: savingsRate > 20 ? "Excellent! Keep it up" : savingsRate > 10 ? "Good, room to improve" : "Consider reducing expenses",
      bg: savingsRate > 20 ? "bg-accent-green/5" : "bg-accent-red/5",
      border: savingsRate > 20 ? "border-accent-green/20" : "border-accent-red/20",
      iconColor: savingsRate > 20 ? "text-accent-green" : "text-accent-red",
    },
    {
      icon: BarChart2,
      color: "cyan",
      title: "Monthly Expense Change",
      value: stats.expenseChange > 0 ? `+${stats.expenseChange}%` : `${stats.expenseChange}%`,
      detail: `vs previous month`,
      bg: "bg-accent-cyan/5", border: "border-accent-cyan/20", iconColor: "text-accent-cyan",
    },
    {
      icon: Target,
      color: "purple",
      title: "Avg Monthly Spend",
      value: `$${stats.monthly.length ? Math.round(stats.monthly.reduce((s, m) => s + m.expenses, 0) / stats.monthly.length).toLocaleString() : 0}`,
      detail: "Based on transaction history",
      bg: "bg-accent-purple/5", border: "border-accent-purple/20", iconColor: "text-accent-purple",
    },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h2 className="font-display font-bold text-2xl text-white mb-1">Insights</h2>
        <p className="text-sm font-body text-white/30">Smart observations from your financial data</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        {insights.map(({ icon: Icon, title, value, detail, bg, border, iconColor }, i) => (
          <div key={title} style={{ animationDelay: `${i * 80}ms` }}
            className={`animate-slide-up bg-bg-card ${border} border rounded-2xl p-5 hover:-translate-y-1 transition-all duration-300`}>
            <div className={`w-9 h-9 rounded-xl ${bg} border ${border} flex items-center justify-center mb-4`}>
              <Icon size={17} className={iconColor} />
            </div>
            <p className="text-xs font-body text-white/30 uppercase tracking-widest mb-1">{title}</p>
            <p className="font-display font-bold text-xl text-white mb-1">{value}</p>
            <p className="text-xs font-body text-white/30">{detail}</p>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-bg-card border border-border rounded-2xl p-5 animate-slide-up" style={{ animationDelay: "400ms" }}>
          <h3 className="font-display font-semibold text-white text-base mb-5">Spending by Category</h3>
          <div className="space-y-3">
            {stats.categories.length === 0 ? (
              <p className="text-white/20 font-body text-sm text-center py-8">No expense data available</p>
            ) : stats.categories.map(({ name, value, color }, i) => {
              const total = stats.expenses;
              const pct = total > 0 ? ((value / total) * 100).toFixed(1) : 0;
              return (
                <div key={name}>
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-sm font-body text-white/70">{name}</span>
                    <div className="flex items-center gap-3">
                      <span className="text-xs font-body text-white/30">{pct}%</span>
                      <span className="text-sm font-mono text-white/70">${value.toLocaleString()}</span>
                    </div>
                  </div>
                  <div className="h-1.5 bg-white/5 rounded-full overflow-hidden">
                    <div className="h-full rounded-full transition-all duration-1000"
                      style={{ width: `${pct}%`, backgroundColor: color }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="bg-bg-card border border-border rounded-2xl p-5 animate-slide-up" style={{ animationDelay: "500ms" }}>
          <h3 className="font-display font-semibold text-white text-base mb-2">Spending Profile</h3>
          <p className="text-xs font-body text-white/30 mb-4">Category distribution radar</p>
          {stats.radarData.length === 0 ? (
            <p className="text-white/20 font-body text-sm text-center py-16">No data available</p>
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <RadarChart data={stats.radarData}>
                <PolarGrid stroke="rgba(255,255,255,0.05)" />
                <PolarAngleAxis dataKey="subject" tick={{ fill: "rgba(255,255,255,0.3)", fontSize: 12, fontFamily: "DM Sans" }} />
                <Radar dataKey="value" stroke="#00E5FF" fill="#00E5FF" fillOpacity={0.1} strokeWidth={2} dot={{ fill: "#00E5FF", r: 4 }} />
                <Tooltip contentStyle={tooltipStyle} formatter={(v) => [`$${v.toLocaleString()}`, "Amount"]} />
              </RadarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      <div className="bg-bg-card border border-border rounded-2xl p-5 animate-slide-up" style={{ animationDelay: "600ms" }}>
        <h3 className="font-display font-semibold text-white text-base mb-5">Monthly Comparison</h3>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border">
                {["Month", "Income", "Expenses", "Net Balance", "Savings Rate"].map((h) => (
                  <th key={h} className="text-left px-3 py-2 text-xs font-body text-white/25 uppercase tracking-widest">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {stats.monthly.map(({ month, income, expenses }) => {
                const net = income - expenses;
                const rate = income > 0 ? ((net / income) * 100).toFixed(1) : 0;
                return (
                  <tr key={month} className="border-b border-border/50 last:border-0 hover:bg-bg-hover transition-colors">
                    <td className="px-3 py-3 text-sm font-body text-white/60 font-medium">{month}</td>
                    <td className="px-3 py-3 text-sm font-mono text-accent-green">+${income.toLocaleString()}</td>
                    <td className="px-3 py-3 text-sm font-mono text-accent-red">-${expenses.toLocaleString()}</td>
                    <td className="px-3 py-3 text-sm font-mono font-medium" style={{ color: net >= 0 ? "#00D4AA" : "#FF4D6D" }}>
                      {net >= 0 ? "+" : ""}${net.toLocaleString()}
                    </td>
                    <td className="px-3 py-3">
                      <span className={`text-xs font-body px-2 py-1 rounded-lg ${parseFloat(rate) > 20 ? "bg-accent-green/10 text-accent-green" : "bg-accent-red/10 text-accent-red"
                        }`}>{rate}%</span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
